import { fetchAuthSession } from 'aws-amplify/auth'
import moment from 'moment'
import { useCallback } from 'react'
import { Dispatch } from 'redux'
import { ExtendAnalyticsBrowser } from 'src/analytics/AnalyticsContext'
import { AuthTypes } from 'src/enums'
import { useAuthTokens } from 'src/hooks/useAuthTokens'
import { deleteCognitoSignout } from 'src/redux/actions/auth.actions'

type UseCognitoSessionValidArgs = {
  localAuthBypass: boolean
  authType: string | undefined
  isCspAuthenticated: boolean
  decodedJwt: Record<string, any> | undefined
  decodedCspJwt: Record<string, any> | undefined
  analytics: ExtendAnalyticsBrowser
  // Matches the type `useDispatch()` resolves to at the App.tsx call site
  // (its first overload's default, `Dispatch<any>`) — accepts thunk actions
  // without depending on the unused `redux-thunk/extend-redux` augmentation.
  dispatch: Dispatch<any>
}

/**
 * Cognito-only session-validity check for App.tsx's 401 interceptor and its
 * on-auth-status effect. The active JWT existence check reads through
 * `useAuthTokens` — the reactive tokenStore boundary — so a revert to a
 * Redux-sourced token here fails
 * `refreshInterceptor.characterization.test.ts`.
 */
export const useCognitoSessionValid = ({
  localAuthBypass,
  authType,
  isCspAuthenticated,
  decodedJwt,
  decodedCspJwt,
  analytics,
  dispatch,
}: UseCognitoSessionValidArgs): (() => Promise<void>) => {
  const { jwt, cspJwt } = useAuthTokens()

  return useCallback(async () => {
    // Skip session validation in local auth bypass mode
    if (localAuthBypass) {
      return
    }
    // This function only handles Cognito authentication - Auth0 has its own session handling
    if (authType !== AuthTypes.cognito) {
      return
    }
    let hasAmplifySession = false
    try {
      const session = await fetchAuthSession()
      hasAmplifySession = !!session.tokens
    } catch {
      hasAmplifySession = false
    }

    if (hasAmplifySession) {
      return
    }

    const activeJwt = isCspAuthenticated ? cspJwt : jwt
    const activeDecodedJwt = isCspAuthenticated ? decodedCspJwt : decodedJwt
    const isJwtStillValid =
      activeJwt !== undefined &&
      activeDecodedJwt !== undefined &&
      moment(new Date(activeDecodedJwt.exp * 1000)).isAfter(moment())

    if (!isJwtStillValid) {
      dispatch(deleteCognitoSignout(true, analytics, isCspAuthenticated))
    }
  }, [
    analytics,
    authType,
    cspJwt,
    decodedCspJwt,
    decodedJwt,
    dispatch,
    isCspAuthenticated,
    jwt,
    localAuthBypass,
  ])
}
